import apiClient from './apiClient';

const apiBaseURL = '/api/configuration';

type ConfigurationFilesResponse = {
  files?: Record<string, string | null>;
};

type ConfigurationTemplatesResponse = {
  templates?: Array<{
    id: string;
    name: string;
    description: string;
    file_count: number;
  }>;
};

type LoadTemplateResponse = {
  file_count: number;
};

export type ImportConfigurationZipResponse = {
  message: string;
  organization_id: string;
  bucket: string;
  files_uploaded: string[];
};

export type LLMRuntimeFeatureSettings = {
  /** Id of the organization_llm_provider row this service is bound to. */
  provider_id: string | null;
  /** Human label of that provider, for display only. */
  provider_label: string | null;
  provider: string | null;
  model: string | null;
  max_tokens: number | null;
  base_url: string | null;
  has_api_key: boolean;
};

export type LLMRuntimeSettingsResponse = {
  organization_id: string;
  defaults: LLMRuntimeFeatureSettings;
  features: Record<string, LLMRuntimeFeatureSettings>;
};

export type LLMRuntimeFeaturePatch = {
  /** Preferred: bind the service to a configured provider. */
  provider_id?: string;
  /** Legacy inline connection, kept for backwards compatibility. */
  provider?: string;
  model?: string;
  max_tokens?: number;
  api_key?: string;
};

export type LLMRuntimeSettingsUpdateRequest = {
  defaults?: LLMRuntimeFeaturePatch;
  features?: Record<string, LLMRuntimeFeaturePatch>;
};

export type LLMModelCatalogEntry = {
  id: string;
  label: string;
  max_output_tokens: number;
  default_max_tokens: number;
  doc_url: string;
};

export type LLMEmbeddingModelCatalogEntry = {
  id: string;
  label: string;
  dimensions: number;
  doc_url: string;
};

export type LLMModelCatalogResponse = {
  providers: Record<
    string,
    {
      provider: string;
      docs_url: string;
      models: LLMModelCatalogEntry[];
    }
  >;
  /** OpenAI-only embedding models, surfaced in the Document indexing card. */
  embedding_models: LLMEmbeddingModelCatalogEntry[];
};

/** A provider connection configured by an org-admin. */
export type LLMProvider = {
  id: string;
  organization_id: string;
  provider_type: string;
  label: string;
  base_url: string | null;
  /** Credentials are write-only: only presence and a fingerprint come back. */
  has_api_key: boolean;
  api_key_last4: string | null;
  extra_config: Record<string, unknown>;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
};

export type LLMProviderListResponse = {
  providers: LLMProvider[];
};

export type LLMProviderCreateRequest = {
  provider_type: string;
  label: string;
  base_url?: string | null;
  api_key?: string;
  extra_config?: Record<string, unknown>;
  is_active?: boolean;
};

export type LLMProviderUpdateRequest = {
  label?: string;
  base_url?: string | null;
  /** Omit to keep the stored key; set `remove_api_key` to clear it. */
  api_key?: string;
  remove_api_key?: boolean;
  extra_config?: Record<string, unknown>;
  is_active?: boolean;
};

export type LLMProviderTestResult = {
  ok: boolean;
  latency_ms: number | null;
  detail: string | null;
  error: string | null;
};

/** Static descriptor driving the provider form: required fields and capabilities. */
export type LLMProviderTypeDescriptor = {
  provider_type: string;
  label: string;
  default_base_url: string | null;
  requires_base_url: boolean;
  supports_tools: boolean;
  supports_json_mode_with_tools: boolean;
  supports_embeddings: boolean;
  supports_streaming: boolean;
  extra_fields: string[];
  docs_url: string | null;
  /** Authenticates with the app's Databricks identity: an API key is optional. */
  uses_workspace_identity?: boolean;
};

export type LLMProviderTypeCatalogResponse = {
  provider_types: LLMProviderTypeDescriptor[];
};

export type IndexingSettingsResponse = {
  /** null = inherit environment default */
  ontology_extraction_enabled: boolean | null;
  env_default: boolean;
  effective: boolean;
  /** null = inherit default 2 (UC-3.83) */
  rag_graph_hop_limit?: number | null;
  effective_rag_graph_hop_limit?: number;
  rag_graph_hop_limit_default?: number;
  embedding_model?: string;
  embedding_provider?: string;
  /** Vector width the org's Neo4j index was created with. */
  index_dimensions?: number;
  /** True when the selected embedding model's width does not fit that index. */
  requires_reindex?: boolean;
  dimension_conflict?: {
    model: string;
    model_dimensions: number;
    index_dimensions: number;
  } | null;
};

export type IndexingSettingsUpdateRequest = {
  ontology_extraction_enabled?: boolean | null;
  rag_graph_hop_limit?: number | null;
};

const configurationService = {
  getOrganizationConfiguration: async (organizationId: string): Promise<ConfigurationFilesResponse> => {
    const response = await apiClient.get<ConfigurationFilesResponse>(`${apiBaseURL}/${organizationId}`);
    return response.data;
  },

  getTemplates: async (): Promise<ConfigurationTemplatesResponse> => {
    const response = await apiClient.get<ConfigurationTemplatesResponse>(`${apiBaseURL}/templates`);
    return response.data;
  },

  uploadConfigurationFile: async (
    organizationId: string,
    fileId: string,
    file: File
  ): Promise<void> => {
    const formData = new FormData();
    formData.append(fileId, file);
    await apiClient.post(`${apiBaseURL}/upload/${organizationId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importConfigurationZip: async (organizationId: string, archive: File): Promise<ImportConfigurationZipResponse> => {
    const formData = new FormData();
    formData.append('archive', archive);
    const response = await apiClient.post<ImportConfigurationZipResponse>(
      `${apiBaseURL}/import-zip/${organizationId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  getConfigurationFileContent: async (organizationId: string, fileId: string): Promise<string> => {
    const response = await apiClient.get<string>(`${apiBaseURL}/${organizationId}/${fileId}`);
    return response.data;
  },

  loadTemplate: async (organizationId: string, templateId: string): Promise<LoadTemplateResponse> => {
    const response = await apiClient.post<LoadTemplateResponse>(
      `${apiBaseURL}/load-template/${organizationId}?template_id=${templateId}`
    );
    return response.data;
  },

  getLLMRuntimeSettings: async (organizationId: string): Promise<LLMRuntimeSettingsResponse> => {
    const response = await apiClient.get<LLMRuntimeSettingsResponse>(
      `${apiBaseURL}/${organizationId}/settings/llm-runtime`
    );
    return response.data;
  },

  updateLLMRuntimeSettings: async (
    organizationId: string,
    payload: LLMRuntimeSettingsUpdateRequest
  ): Promise<LLMRuntimeSettingsResponse> => {
    const response = await apiClient.put<LLMRuntimeSettingsResponse>(
      `${apiBaseURL}/${organizationId}/settings/llm-runtime`,
      payload
    );
    return response.data;
  },

  getLLMModelCatalog: async (): Promise<LLMModelCatalogResponse> => {
    const response = await apiClient.get<LLMModelCatalogResponse>(
      `${apiBaseURL}/settings/llm/model-catalog`
    );
    return response.data;
  },

  getLLMProviderTypes: async (): Promise<LLMProviderTypeCatalogResponse> => {
    const response = await apiClient.get<LLMProviderTypeCatalogResponse>(
      `${apiBaseURL}/settings/llm/provider-types`
    );
    return response.data;
  },

  getLLMProviders: async (organizationId: string): Promise<LLMProviderListResponse> => {
    const response = await apiClient.get<LLMProviderListResponse>(
      `${apiBaseURL}/${organizationId}/llm-providers`
    );
    return response.data;
  },

  createLLMProvider: async (
    organizationId: string,
    payload: LLMProviderCreateRequest
  ): Promise<LLMProvider> => {
    const response = await apiClient.post<LLMProvider>(
      `${apiBaseURL}/${organizationId}/llm-providers`,
      payload
    );
    return response.data;
  },

  updateLLMProvider: async (
    organizationId: string,
    providerId: string,
    payload: LLMProviderUpdateRequest
  ): Promise<LLMProvider> => {
    const response = await apiClient.patch<LLMProvider>(
      `${apiBaseURL}/${organizationId}/llm-providers/${providerId}`,
      payload
    );
    return response.data;
  },

  deleteLLMProvider: async (organizationId: string, providerId: string): Promise<void> => {
    await apiClient.delete(`${apiBaseURL}/${organizationId}/llm-providers/${providerId}`);
  },

  testLLMProvider: async (
    organizationId: string,
    providerId: string
  ): Promise<LLMProviderTestResult> => {
    const response = await apiClient.post<LLMProviderTestResult>(
      `${apiBaseURL}/${organizationId}/llm-providers/${providerId}/test`
    );
    return response.data;
  },

  getIndexingSettings: async (organizationId: string): Promise<IndexingSettingsResponse> => {
    const response = await apiClient.get<IndexingSettingsResponse>(
      `${apiBaseURL}/${organizationId}/settings/indexing`
    );
    return response.data;
  },

  updateIndexingSettings: async (
    organizationId: string,
    payload: IndexingSettingsUpdateRequest
  ): Promise<IndexingSettingsResponse> => {
    const response = await apiClient.put<IndexingSettingsResponse>(
      `${apiBaseURL}/${organizationId}/settings/indexing`,
      payload
    );
    return response.data;
  },
};

export default configurationService;
